/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Hyunsu Sim (hssim@rsupport.com)
 *
 * @description
 *
 * @created at Thu Oct 10 2024
 **/

import { Menu, useModal } from "@/frameworks/designStuff/components";
import { _T } from "@rfice/language";

import { useChat } from "@/frameworks/core/API";
import { useConfirm, usePopover } from "@/frameworks/core/hooks";
import { ModalID, POPOVER_ID, ZIndex } from "@/frameworks/core/commons";

import * as S from "../../emotion";
import ChatVote from "../../../ChatVote";

type MoreMenu = "edit" | "end" | "delete";
type VoteMore = {
	voteID: string;
	roomID: string;
};

export default function VoteMore({ voteID, roomID }: VoteMore) {
	const chatAPI = useChat();
	const popover = usePopover();
	const modal = useModal();
	const { confirm } = useConfirm();

	const handleMenu = (e: React.MouseEvent<HTMLElement>) => {
		const type = e.currentTarget.dataset["type"] as MoreMenu;
		switch (type) {
			case "edit":
				modal.show({
					id: ModalID.CHAT_VOTE,
					content: <ChatVote roomID={roomID} voteID={voteID} isUpdate={true} />,
					zIndex: ZIndex.SIDEBAR + 1
				});
				break;
			case "end":
				confirm(_T("투표를 종료하시겠습니까?"), {
					async onConfirm() {
						await chatAPI.vote.end.execute({ voteId: voteID, roomId: roomID });
					}
				});
				break;
			case "delete":
				confirm(
					{
						title: _T("투표 삭제"),
						descriptions: _T("정말 투표를 삭제하시겠습니까?"),
						confirmText: _T("예"),
						cancelText: _T("아니오")
					},
					{
						async onConfirm() {
							await chatAPI.vote.delete.execute({ voteId: voteID, roomId: roomID });
						}
					}
				);

				break;
		}

		popover.hide(POPOVER_ID.VOTE_MORE);
	};

	return (
		<S.VoteMoreWrapper>
			<Menu.List>
				<Menu.Item typoSize="Button2" typoColor="neutral_80_100" onClick={handleMenu} data-type="end">
					{_T("투표종료")}
				</Menu.Item>
				<Menu.Item typoSize="Button2" typoColor="error_60_100" onClick={handleMenu} data-type="delete">
					{_T("투표삭제")}
				</Menu.Item>
			</Menu.List>
		</S.VoteMoreWrapper>
	);
}
